import React, { useState, useEffect } from 'react';
import {
  HiXMark,
  HiPencil,
  HiCheck,
  HiUserGroup,
  HiEnvelope,
  HiCheckBadge,
  HiCalendar,
  HiBuildingOffice2,
  HiPaperAirplane,
  HiCheckCircle
} from 'react-icons/hi2';
import { CoachWithSubscription, Organisation } from '../hooks/useCoachesManagement';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { userService } from '../services/userService';
import { UserData } from '../types/user';
import { invitationService } from '../services/invitationService';
import { Invitation } from '../types/invitation';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { BuildingOfficeIcon, CalendarDateRangeIcon, CheckBadgeIcon, EnvelopeIcon, IdentificationIcon, UserIcon } from '@heroicons/react/24/outline';
import { PencilIcon } from '@heroicons/react/24/solid';

interface CoachDetailsModalProps {
  coach: CoachWithSubscription;
  organisation: Organisation | null;
  onClose: () => void;
  onUpdate: () => void;
}

const CoachDetailsModal: React.FC<CoachDetailsModalProps> = ({
  coach,
  organisation,
  onClose,
  onUpdate
}) => {
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [orgName, setOrgName] = useState(organisation?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberProfiles, setMemberProfiles] = useState<Map<string, UserData>>(new Map());
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [processingInvitations, setProcessingInvitations] = useState<Set<string>>(new Set());
  const [creatingOrganisation, setCreatingOrganisation] = useState(false);
  const [newAthleteEmail, setNewAthleteEmail] = useState('');
  const [sendingInvitation, setSendingInvitation] = useState(false);

  // Fetch member profiles when organisation changes
  useEffect(() => {
    const fetchMemberProfiles = async () => {
      if (!organisation || organisation.members.length === 0) {
        setMemberProfiles(new Map());
        return;
      }

      setLoadingMembers(true);

      try {
        const profiles = new Map<string, UserData>();

        // Fetch profiles in batches to avoid rate limits
        const BATCH_SIZE = 10;
        const memberIds = organisation.members.map(m => m.userId);

        for (let i = 0; i < memberIds.length; i += BATCH_SIZE) {
          const batch = memberIds.slice(i, i + BATCH_SIZE);

          const batchResults = await Promise.all(
            batch.map(async (userId) => {
              try {
                const userData = await userService.getUserData(userId);
                return { userId, userData };
              } catch (error) {
                console.error(`Error fetching user ${userId}:`, error);
                return { userId, userData: null };
              }
            })
          );

          batchResults.forEach(({ userId, userData }) => {
            if (userData) {
              profiles.set(userId, userData);
            }
          });
        }

        setMemberProfiles(profiles);
      } catch (error) {
        console.error('Error fetching member profiles:', error);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchMemberProfiles();
  }, [organisation]);

  // Fetch invitations sent by this coach
  useEffect(() => {
    const fetchInvitations = async () => {
      setLoadingInvitations(true);
      try {
        const coachInvitations = await invitationService.getInvitationsByCoach(organisation?.ownerId);
        setInvitations(coachInvitations);
      } catch (error) {
        console.error('Error fetching invitations:', error);
      } finally {
        setLoadingInvitations(false);
      }
    };

    fetchInvitations();
  }, [organisation?.ownerId]);


  const handleSaveOrganisation = async () => {
    if (!organisation) return;

    setSaving(true);
    setError(null);

    try {
      const orgRef = doc(db, 'organisations', organisation.id);
      await updateDoc(orgRef, {
        name: orgName,
      });

      setIsEditingOrg(false);
      onUpdate();
    } catch (err) {
      console.error('Error updating organisation:', err);
      setError('Failed to update organisation. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setOrgName(organisation?.name || '');
    setIsEditingOrg(false);
    setError(null);
  };

  const formatDate = (date: Date | undefined) => {
    console.log('Formatting date:', date);
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const handleAcceptInvitation = async (invitation: Invitation) => {
    if (!invitation.id) return;

    setProcessingInvitations(prev => new Set(prev).add(invitation.id!));
    setError(null);

    try {
      // Call the cloud function to manually add athlete
      const manuallyAddAthlete = httpsCallable(functions, 'manuallyAddAthlete');
      const result = await manuallyAddAthlete({ invitationId: invitation.id });

      console.log('Athlete added successfully:', result.data);

      // Refresh invitations and trigger parent update to refresh members
      const updatedInvitations = await invitationService.getInvitationsByCoach(organisation?.ownerId);
      setInvitations(updatedInvitations);
      onUpdate();
    } catch (err: any) {
      console.error('Error accepting invitation:', err);

      // Extract error message from Firebase Functions error
      const errorMessage = err.message || 'Failed to accept invitation. Please try again.';
      setError(errorMessage);
    } finally {
      setProcessingInvitations(prev => {
        const next = new Set(prev);
        next.delete(invitation.id!);
        return next;
      });
    }
  };

  const handleCreateOrganisation = async () => {
    setCreatingOrganisation(true);
    setError(null);

    try {
      const createOrganisationViaAdmin = httpsCallable(functions, 'createOrganisationViaAdmin');
      await createOrganisationViaAdmin({ userId: coach.id });
      onUpdate();
    } catch (err: any) {
      console.error('Error creating organisation:', err);
      setError(err.message || 'Failed to create organisation. Please try again.');
    } finally {
      setCreatingOrganisation(false);
    }
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newAthleteEmail.trim()) return;

    setSendingInvitation(true);
    setError(null);

    try {
      const inviteAthleteViaAdmin = httpsCallable(functions, 'inviteAthleteViaAdmin');
      await inviteAthleteViaAdmin({
        coachId: coach.id,
        athleteEmail: newAthleteEmail.trim()
      });

      setNewAthleteEmail('');

      // Refresh invitations list
      const updatedInvitations = await invitationService.getInvitationsByCoach(organisation?.ownerId);
      setInvitations(updatedInvitations);
    } catch (err: any) {
      console.error('Error sending invitation:', err);
      setError(err.message || 'Failed to send invitation. Please try again.');
    } finally {
      setSendingInvitation(false);
    }
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold">Coach Details</h3>
          <button
            onClick={onClose}
            className="btn btn-sm !btn-ghost btn-circle"
          >
            <HiXMark className="w-5 h-5" />
          </button>
        </div>

        {/* Coach Information */}
        <div className="card bg-base-200 mb-6">
          <div className="card-body">
            <div className="flex items-center gap-4">
              {coach.userData.imageUrlNormal || coach.userData.imageUrl ? (
                <img
                  src={coach.userData.imageUrlNormal || coach.userData.imageUrl}
                  alt={`${coach.userData.name}'s avatar`}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-info text-white flex items-center justify-center text-2xl font-semibold">
                  {coach.userData.name.charAt(0).toUpperCase()}
                </div>
              )}

              <div className="flex-1">
                <h4 className="text-xl font-semibold mb-2">
                  {coach.userData.name} {coach.userData.surname}
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="w-4 h-4 text-base-content/60" />
                    <span>{coach.userData.email}</span>
                  </div>

                  <div className="flex items-center gap-2">

                  </div>

                  <div className="flex items-center gap-2">
                    <IdentificationIcon className="w-4 h-4 text-base-content/60" />
                    <span className="text-sm">
                      {coach.id}
                    </span>
                  </div>

                  {coach.subscription && (
                    <div className="flex items-center gap-2">
                      <CheckBadgeIcon className="w-4 h-4 text-base-content/60" />
                      <span className={`badge uppercase text-[10px] h-6 ${
                        coach.subscription.subscriptionVia === 'admin' ? 'bg-base-200 text-base-content/80' :
                        coach.subscription.subscriptionVia === 'stripe' ? 'bg-blue-200 text-blue-600' :
                        'bg-base-200 text-base-content/80'
                      }`}>
                        {coach.subscription.subscriptionType}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organisation Information */}
        {organisation ? (
          <div className="card bg-base-200">
            <div className="card-body">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BuildingOfficeIcon className="w-5 h-5 text-base-content/60" />
                  <h4 className="text-lg font-semibold">Organisation</h4>
                </div>

                {!isEditingOrg ? (
                  <button
                    onClick={() => setIsEditingOrg(true)}
                    className="btn btn-sm !btn-ghost gap-2"
                  >
                    <PencilIcon className="w-4 h-4" />
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancelEdit}
                      className="btn btn-sm !btn-ghost"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveOrganisation}
                      className="btn btn-sm btn-primary gap-2"
                      disabled={saving}
                    >
                      {saving ? (
                        <span className="loading loading-spinner loading-xs"></span>
                      ) : (
                        <HiCheck className="w-4 h-4" />
                      )}
                      Save
                    </button>
                  </div>
                )}
              </div>

              {error && (
                <div className="alert alert-error mb-4">
                  <span>{error}</span>
                </div>
              )}

              {isEditingOrg ? (
                <div className="space-y-4">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Organisation Name</span>
                    </label>
                    <input
                      type="text"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      className="input input-bordered"
                      placeholder="Enter organisation name"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-base-content/80 mb-1">Name</div>
                    <div className="font-medium">{organisation.name}</div>
                  </div>

                  {organisation.description && (
                    <div>
                      <div className="text-sm text-base-content/80 mb-1">Description</div>
                      <div className="text-sm">{organisation.description}</div>
                    </div>
                  )}

                  <div className="flex items-center gap-8 text-sm">
                    <div className="flex items-center gap-1">
                      <IdentificationIcon className="w-4 h-4 text-base-content/60" />
                      <span className="text-sm bg-base-200">
                        {organisation.id}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Members List */}
              <div className="divider"></div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <HiUserGroup className="w-5 h-5 text-base-content/60" />
                  <h5 className="font-semibold">
                    Members ({organisation.members.length})
                  </h5>
                </div>

                {loadingMembers ? (
                  <div className="flex justify-center py-8">
                    <span className="loading loading-spinner loading-md"></span>
                  </div>
                ) : organisation.members.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {organisation.members.map((member) => {
                      const profile = memberProfiles.get(member.userId);
                      return (
                        <div
                          key={member.userId}
                          className="flex items-center justify-between p-3 bg-base-100 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {profile?.imageUrl ? (
                              <img
                                src={profile.imageUrl}
                                alt={`${profile.name}'s avatar`}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-info text-white flex items-center justify-center text-sm font-semibold">
                                {profile?.name?.charAt(0).toUpperCase() || member.userId.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-sm">
                                {profile ? `${profile.name} ${profile.surname}` : member.userId}
                              </div>
                              <div className="flex items-center gap-2">
                                {profile?.email && (
                                  <div className="text-xs text-base-content/60">
                                    {profile.email} •
                                  </div>
                                )}
                                <div className="text-xs text-base-content/60">
                                  {member.userId}
                                </div>
                                {member.role && (
                                  <div className="text-xs text-base-content/60 capitalize">
                                    • {member.role}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-base-content/60">
                    No members found
                  </div>
                )}
              </div>

              {/* Invitations List */}
              <div className="divider"></div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <HiPaperAirplane className="w-5 h-5 text-base-content/60" />
                  <h5 className="font-semibold">
                    Invitations Sent ({invitations.length})
                  </h5>
                </div>

                {/* Send Invitation Form */}
                <form onSubmit={handleSendInvitation} className="flex gap-2 mb-4">
                  <input
                    type="email"
                    value={newAthleteEmail}
                    onChange={(e) => setNewAthleteEmail(e.target.value)}
                    placeholder="Enter athlete email"
                    className="input input-bordered input-sm flex-1"
                    disabled={sendingInvitation}
                  />
                  <button
                    type="submit"
                    disabled={sendingInvitation || !newAthleteEmail.trim()}
                    className="btn btn-sm btn-primary gap-1"
                  >
                    {sendingInvitation ? (
                      <span className="loading loading-spinner loading-xs"></span>
                    ) : (
                      <HiPaperAirplane className="w-4 h-4" />
                    )}
                    Send
                  </button>
                </form>

                {loadingInvitations ? (
                  <div className="flex justify-center py-8">
                    <span className="loading loading-spinner loading-md"></span>
                  </div>
                ) : invitations.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {invitations.map((invitation) => {
                      const isProcessing = processingInvitations.has(invitation.id || '');
                      const isPending = invitation.status === 'pending';

                      return (
                        <div
                          key={invitation.id}
                          className="flex items-center justify-between p-3 bg-base-100 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-sm">
                                {invitation.athleteEmail}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="text-xs text-base-content/60">
                                { invitation.id } •
                              </div>
                              <div className="text-xs text-base-content/60">
                                {formatDate(invitation.createdAt)} •
                              </div>
                              <span
                                className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${
                                  invitation.status === 'accepted'
                                    ? 'bg-green-500/15 text-green-600'
                                    : invitation.status === 'pending'
                                    ? 'bg-yellow-500/15 text-yellow-600'
                                    : invitation.status === 'expired'
                                    ? 'badge-warning'
                                    : invitation.status === 'deleted'
                                    ? 'bg-red-500/15 text-red-600'
                                    : 'bg-base-200 text-base-content/80'
                                }`}
                              >
                                {invitation.status}
                              </span>
                            </div>
                            {invitation.message && (
                              <div className="text-xs text-base-content/80 mt-1 italic">
                                "{invitation.message}"
                              </div>
                            )}
                          </div>

                          {/* Accept button for pending invitations */}
                          {isPending && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAcceptInvitation(invitation)}
                                disabled={isProcessing}
                                className="btn btn-sm btn-primary gap-1"
                                title="Manually accept invitation and add athlete"
                              >
                                {isProcessing ? (
                                  <span className="loading loading-spinner loading-xs"></span>
                                ) : (
                                  <HiCheckCircle className="w-4 h-4" />
                                )}
                                Accept
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-base-content/60">
                    No invitations sent
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="card bg-base-200">
            <div className="card-body">
              <div className="flex items-center gap-2 mb-2">
                <HiBuildingOffice2 className="w-5 h-5 text-base-content/60" />
                <h4 className="text-lg font-semibold">Organisation</h4>
              </div>
              <p className="text-base-content/80 mb-4">
                This coach is not associated with any organisation.
              </p>
              {error && (
                <div className="alert alert-error mb-4">
                  <span>{error}</span>
                </div>
              )}
              <button
                onClick={handleCreateOrganisation}
                disabled={creatingOrganisation}
                className="btn btn-primary gap-2"
              >
                {creatingOrganisation ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  <HiBuildingOffice2 className="w-4 h-4" />
                )}
                Create Organisation
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="modal-action">
          <button onClick={onClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
};

export default CoachDetailsModal;
